<template>
  <div class="inline-image-viewer d-flex justify-content-center align-items-center" :style="{ width: viewerWidth, height: viewerHeight }">
    <div class="inline-image-viewer-content">
      <div class="inline-image-viewer-content--header d-flex justify-content-between align-items-center px-3">
        <div class="header-left">
          <strong>{{ loading ? 'Attachments(Loading...)' : 'Attachments' }}</strong>
        </div>
        <div class="header-right">
          <button type="button" aria-label="Close" class="close" id="id-modal-close-button" @click="$emit('close')">
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
      <div class="inline-image-viewer-content--body">
        <div v-if="!viewed" class="loading-spinner d-flex justify-content-center align-items-center">
          <div class="spinner-border" role="status" />
        </div>
        <viewer
          v-if="isResized"
          :options="options"
          :images="images"
          @inited="inited"
        >
          <template #default="scope">
            <img
              v-for="image in scope.images" :key="image.attachmentId"
              class="image__item"
              :src="image.source"
              :data-src="image.source"
            />
          </template>
        </viewer>
      </div>
      <div class="inline-image-viewer-content--footer d-flex justify-content-center align-items-center px-3">
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          id="inline-image-viewer-meta"
          v-b-tooltip.hover
          title="Show metadata"
        >
          <font-awesome-icon icon="fas fa-info" />
        </button>
        <b-popover target="inline-image-viewer-meta" triggers="click">
          <div class="inline-viewer-meta-info">
            {{ getMetadata }}
          </div>
        </b-popover>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="zoomIn"
          v-b-tooltip.hover
          title="Zoom In"
        >
          <font-awesome-icon icon="fas fa-plus" />
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="zoomOut"
          v-b-tooltip.hover
          title="Zoom Out"
        >
          <font-awesome-icon icon="fas fa-minus" />
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="fullSize"
          v-b-tooltip.hover
          title="Full Size"
        >
          1:1
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="reset"
          v-b-tooltip.hover
          title="Reset"
        >
          <font-awesome-icon icon="fas fa-redo-alt" />
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="prev"
          v-b-tooltip.hover
          title="Prev"
        >
          <font-awesome-icon icon="fas fa-chevron-left" />
        </button>
        <button
          type="button"
          class="btn btn-sm btn-rounded bg-info text-white custom-button-large"
          @click="play"
          v-b-tooltip.hover
          :title="playing ? 'Stop' : 'Play'"
        >
          <font-awesome-icon v-if="!playing" icon="fas fa-play" />
          <font-awesome-icon v-else icon="fas fa-stop" />
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="next"
          v-b-tooltip.hover
          title="Next"
        >
          <font-awesome-icon icon="fas fa-chevron-right" />
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="rotateLeft"
          v-b-tooltip.hover
          title="Rotate Left"
        >
          <font-awesome-icon icon="fas fa-reply" />
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="rotateRight"
          v-b-tooltip.hover
          title="Rotate Right"
        >
          <font-awesome-icon icon="fas fa-share" />
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="flipH"
          v-b-tooltip.hover
          title="Flip Horizontally"
        >
          <font-awesome-icon icon="fas fa-arrows-alt-h" />
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="flipV"
          v-b-tooltip.hover
          title="Flip Vertically"
        >
          <font-awesome-icon icon="fas fa-arrows-alt-v" />
        </button>
        <button
          type="button"
          class="btn btn-xs btn-rounded bg-info text-white custom-button"
          @click="download"
          v-b-tooltip.hover
          title="Download"
        >
          <font-awesome-icon icon="fas fa-download" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters, mapState } from "vuex";
import "viewerjs/dist/viewer.css";
import { component as Viewer } from "v-viewer";

export default {
  name: "InlineImageViewer",
  components: { Viewer },
  props: {
    currentAttachmentId: {
      type: String,
      default: null,
    },
    viewerWidth: String,
    viewerHeight: String,
    resizing: Boolean,
  },
  computed: {
    ...mapGetters("conversations", ["conversationAttachments"]),
    ...mapGetters("user", ["currentToken"]),
    ...mapState({
      imageAttachmentSource: state => state.attachment.imageAttachmentSource
    }),
    attachments() {
      const attachments = [...this.conversationAttachments].filter(attachment => attachment.content_type.includes("image"));
      return attachments.sort((a, b) => a.created_at - b.created_at).map(attachment => ({
        source: `${this.$apihost}/attachments/${attachment.attachment_id}/download?stream=1`,
        attachmentId: attachment.attachment_id,
        metadata: attachment.metadata,
      }));
    },
    initialViewIndex() {
      const attachments = [...this.conversationAttachments].filter(attachment => attachment.content_type.includes("image"));
      return attachments.sort((a, b) => a.created_at - b.created_at).findIndex(attachment => attachment.attachment_id == this.currentAttachmentId);
    },
    getMetadata() {
      if (this.images.length > 0) {
        const currentIndex = this.lastViewed > -1 ? this.lastViewed : this.initialViewIndex;
        if (currentIndex < this.images.length) {
          return this.images[currentIndex].metadata;
        }
        return this.images[0].metadata;
      }
      return null;
    },
  },
  data() {
    return {
      images: [],
      options: {
        inline: true,
        url: "data-src",
        button: false,
        toolbar: false,
        backdrop: false,
        title: false,
        viewed: (e) => {
          if (this.viewed) {
            this.lastViewed = e.detail.index;
          }
          this.lastViewedImageTitle = e.detail.image.alt;
          this.viewed = true;
        },
      },
      isResized: true,
      lastViewed: -1,
      lastViewedImageTitle: "",
      viewed: false,
      initiallyViewed: false,
      loading: false,
      playing: false,
      scaleX: 1,
      scaleY: 1,
    };
  },
  created() {
    this.isResized = true;
  },
  methods: {
    inited(viewer) {
      this.$viewer = viewer;
      this.isResized = true;
      if (this.lastViewed > -1) {
        this.$viewer.view(this.lastViewed);
      } else {
        this.$viewer.view(this.initialViewIndex);
      }
    },
    showMetadata() {},
    zoomIn() {
      if (this.$viewer) {
        this.$viewer.zoom(0.5);
      }
    },
    zoomOut() {
      if (this.$viewer) {
        this.$viewer.zoom(-0.5);
      }
    },
    fullSize() {
      if (this.$viewer) {
        this.$viewer.zoomTo(1);
      }
    },
    reset() {
      if (this.$viewer) {
        this.$viewer.reset();
      }
    },
    prev() {
      if (this.$viewer) {
        this.$viewer.prev();
      }
    },
    next() {
      if (this.$viewer) {
        this.$viewer.next();
      }
    },
    play() {
      if (this.$viewer) {
        if (!this.playing) {
          this.$viewer.play();
        } else {
          this.$viewer.stop();
        }
      }
      this.playing = !this.playing;
    },
    rotateLeft() {
      if (this.$viewer) {
        this.$viewer.rotate(-90);
      }
    },
    rotateRight() {
      if (this.$viewer) {
        this.$viewer.rotate(90);
      }
    },
    flipH() {
      if (this.$viewer) {
        this.scaleX = -this.scaleX;
        this.$viewer.scaleX(this.scaleX);
      }
    },
    flipV() {
      if (this.$viewer) {
        this.scaleY = -this.scaleY;
        this.$viewer.scaleY(this.scaleY);
      }
    },
    async download() {
      const headers = {
        "Authorization": `Bearer ${this.currentToken}`
      };
      const lastViewed = this.lastViewed > -1 ? this.lastViewed : this.initialViewIndex;
      const attachmentId = this.attachments[lastViewed].attachmentId;
      axios.get(`${this.$apihost}/attachments/${attachmentId}/download?stream=1`, { headers: headers, responseType: "blob" }).then((res) => {
        const file = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
        const docUrl = document.createElement("a");
        docUrl.href = file;
        docUrl.setAttribute("download", this.lastViewedImageTitle);
        document.body.appendChild(docUrl);
        docUrl.click();
      });
    }
  },
  watch: {
    attachments: {
      async handler(attachments) {
        this.loading = true;
        const attachmentSource = this.imageAttachmentSource[this.$route.params.conversationId];
        const tempImages = attachments.map(attachment => ({
          ...attachment,
          source: attachmentSource && attachmentSource[attachment.attachmentId]
            ? attachmentSource[attachment.attachmentId]
            : attachment.source
        }));
        let retries = 0;
        while(retries < 5) {
          const attachmentsForLoadSource = attachments.filter(attachment => !attachmentSource || !attachmentSource[attachment.attachmentId]);

          if (attachmentsForLoadSource.length == 0) {
            break;
          }

          await Promise.all(attachmentsForLoadSource.map((attachment) => {
            const headers = {
              "Authorization": `Bearer ${this.currentToken}`
            };

            return axios.get(attachment.source, { headers: headers, responseType: "blob" }).then((res) => {
              const image = tempImages.find(img => img.attachmentId == attachment.attachmentId);
              const source = URL.createObjectURL(res.data);
              image.source = source;
              this.$store.commit("attachment/setImageAttachmentSource", {
                conversationId: this.$route.params.conversationId,
                attachmentId: attachment.attachmentId,
                source
              });
            }).catch(() => {
            });
          }));

          retries++;
        }

        this.images = [...tempImages];
        this.loading = false;
      },
      deep: true,
      immediate: true,
    },
    resizing(newVal, oldVal) {
      if (newVal === false && oldVal === true) {
        this.isResized = false;
        setTimeout(() => {this.isResized = true;}, 100);
      }
    },
  }
};
</script>

<style lang="scss">
.inline-image-viewer {
  &-content {
    display: grid;
    grid-template-rows: 2rem calc(100% - 4rem) 2rem;
    width: 100%;
    height: 100%;
    &--header {
      cursor: move;
    }
    &--body {
      width: 100%;
      height: 100%;
      overflow: hidden;
      position: relative;
      background-color: white;

      .loading-spinner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }

      .image__item {
        opacity: 0;
        visibility: hidden;
      }

      .viewer-container {
        width: 100% !important;
        height: 100% !important;
      }
    }
    &--footer {
      cursor: move;
      > button {
        margin-right: 2px;
        &:last-child {
          margin-right: 0;
        }
      }
      .custom-button {
        width: 24px !important;
        height: 24px !important;
        padding: 0 !important;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .custom-button-large {
        width: 28px !important;
        height: 28px !important;
        padding: 0 !important;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
}

.inline-viewer-meta-info {
  max-width: 300px;
  max-height: 200px;
  overflow-y: auto;
}
</style>
